import { FormFields, FormValidationSchema } from '@/common/interfaces';

import type { DomainVisitors } from '@/modules/Domains';

import type { Redirect } from './entities/Redirect.entity';

// Тип кінця редіректу: нода (переживає зміну слага) або довільний шлях-рядок.
export const RedirectEndpointEnum = {
  Node: 'node',
  Path: 'path',
} as const;

export type RedirectEndpointType = (typeof RedirectEndpointEnum)[keyof typeof RedirectEndpointEnum];

// Режим співставлення джерела:
// - exact  — точний збіг шляху (з урахуванням query-параметрів у джерелі);
// - nested — шлях і всі вкладені під ним ведуть на ОДНУ ціль (джерело має існувати як сторінка сайту);
// - prefix — префіксне переписування "/one/* -> /two/*": хвіст шляху переноситься на ціль.
export const RedirectMatchModeEnum = {
  Exact: 'exact',
  Nested: 'nested',
  Prefix: 'prefix',
} as const;

export type RedirectMatchMode = (typeof RedirectMatchModeEnum)[keyof typeof RedirectMatchModeEnum];

export const REDIRECT_MATCH_MODE_OPTIONS: { value: RedirectMatchMode; label: string; description: string }[] = [
  {
    value: RedirectMatchModeEnum.Exact,
    label: 'Точний збіг',
    description: 'Спрацьовує лише на точний шлях (з query, якщо його вказано)'
  },
  {
    value: RedirectMatchModeEnum.Nested,
    label: 'Вкладеність',
    description: 'Шлях і всі вкладені сторінки ведуть на одну ціль (шлях має існувати на сайті)'
  },
  {
    value: RedirectMatchModeEnum.Prefix,
    label: 'Wildcard-редірект',
    description: 'Префіксне переписування: /one/* → /two/*'
  },
];

export const DEFAULT_REDIRECT_MATCH_MODE: RedirectMatchMode = RedirectMatchModeEnum.Exact;

export const getRedirectMatchModeLabel = (matchMode: RedirectMatchMode): string =>
  REDIRECT_MATCH_MODE_OPTIONS.find((option) => option.value === matchMode)?.label ?? matchMode;

export const REDIRECT_STATUS_CODES = [301, 302, 303, 307, 308] as const;

export const REDIRECT_STATUS_CODE_OPTIONS: { value: number; label: string }[] = [
  { value: 301, label: '301 — Moved Permanently' },
  { value: 302, label: '302 — Found' },
  { value: 303, label: '303 — See Other' },
  { value: 307, label: '307 — Temporary Redirect' },
  { value: 308, label: '308 — Permanent Redirect' },
];

export const DEFAULT_REDIRECT_STATUS_CODE = 301;

export enum RedirectFormInputs {
  SourceType = 'sourceType',
  SourceNodeId = 'sourceNodeId',
  SourcePath = 'sourcePath',
  TargetType = 'targetType',
  TargetNodeId = 'targetNodeId',
  TargetPath = 'targetPath',
  StatusCode = 'statusCode',
  MatchMode = 'matchMode',
  ForVisitor = 'forVisitor',
  IsActive = 'isActive',
  SiteId = 'siteId',
}

export type RedirectFormFields = FormFields<typeof RedirectFormInputs>;

export type RedirectFormValidationSchema = FormValidationSchema<RedirectFormFields>;

export type RedirectsListTableDataObj = {
  id: Redirect['id'];
  isActive: Redirect['isActive'];
  matchMode: Redirect['matchMode'];
  statusCode: Redirect['statusCode'];
  forVisitor: Redirect['forVisitor'];
  sourceLabel: string;
  targetLabel: string;
};

export type BulkRedirectByPageItem = {
  pageNodeId: string;
  targetType: RedirectEndpointType;
  targetNodeId?: string;
  targetPath?: string;
  statusCode: number;
  matchMode: RedirectMatchMode;
  forVisitor: DomainVisitors;
  isActive: boolean;
};

export type BulkCreateRedirectsByPagesPayload = {
  redirects: BulkRedirectByPageItem[];
};

export type BulkRedirectByPathItem = {
  pathFrom: string;
  pathTo: string;
  statusCode: number;
  matchMode: RedirectMatchMode;
  forVisitor: DomainVisitors;
  isActive: boolean;
};

export type BulkCreateRedirectsByPathsPayload = {
  redirects: BulkRedirectByPathItem[];
};

export type BulkCreateRedirectsResult = {
  createdCount: number;
  skippedCount: number;
  skipped: { pathFrom: string; pathTo: string }[];
  created: Redirect[];
};

export type BulkUpdateRedirectsActivityPayload = {
  ids: string[];
  isActive: boolean;
};

export type BulkDeleteRedirectsPayload = {
  ids: string[];
};

export interface ParsedRedirectRow {
  key: string;
  line: number;
  raw: string;
  pathFrom: string;
  pathTo: string;
}

export type RedirectRedundantConflict = {
  redirect: Redirect;
  overriddenById: string | null;
};

export type RedirectCyclicConflict = {
  redirect: Redirect;
};

export type RedirectConflicts = {
  redundant: RedirectRedundantConflict[];
  cyclic: RedirectCyclicConflict[];
};
